import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";

/**
 * NOTE:
 * - Backendul poate trimite prețul fie ca `price_cents`, fie ca `price`.
 * - Statusul vine ca `is_active` (nu isActive).
 * - bookingsThisMonth poate lipsi -> tratăm ca 0.
 * - Toate calculele folosesc fallback-uri ca să evităm NaN.
 */

// întoarce prețul în RON ca număr (pentru calcule)
const priceToNumber = (s) => {
  if (s?.price_cents != null) return s.price_cents / 100;
  if (s?.price != null) return Number(s.price) || 0;
  return 0;
};

// întoarce numărul de programări ale serviciului în luna curentă
const bookingsThisMonth = (s) => Number(s?.bookingsThisMonth ?? 0);

// formatează o sumă în RON
const formatRON = (value) =>
  new Intl.NumberFormat("ro-RO", { style: "currency", currency: "RON" }).format(
    Number.isFinite(value) ? value : 0
  );

const ServiceStats = ({ services = [], canSeeStats = false, onOpenTotal }) => {
  const { t } = useTranslation();
  const totalServices = services.length;
  const activeServices = services.filter((s) => !!s?.is_active).length;

  const totalBookings = services.reduce((sum, s) => sum + bookingsThisMonth(s), 0);
  const totalRevenue = services.reduce(
    (sum, s) => sum + bookingsThisMonth(s) * priceToNumber(s),
    0
  );

  const sumPrices = services.reduce((sum, s) => sum + priceToNumber(s), 0);
  const averagePrice = totalServices > 0 ? sumPrices / totalServices : 0;

  const baseStats = [
    {
      id: "total",
      label: t("services.stats.total"),
      value: totalServices,
      icon: "Package",
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      id: "active",
      label: t("services.stats.active"),
      value: activeServices,
      icon: "CheckCircle",
      color: "text-success",
      bgColor: "bg-success/10",
    },
  ];

  const extraStats = [
    {
      id: "bookings",
      label: t("services.stats.bookingsThisMonth"),
      value: totalBookings,
      icon: "Calendar",
      color: "text-accent",
      bgColor: "bg-accent/10",
    },
    {
      id: "revenue",
      label: t("services.stats.revenueThisMonth"),
      value: formatRON(totalRevenue),
      icon: "TrendingUp",
      color: "text-success",
      bgColor: "bg-success/10",
    },
    {
      id: "average",
      label: t("services.stats.avgPrice"),
      value: formatRON(averagePrice),
      icon: "DollarSign",
      color: "text-secondary",
      bgColor: "bg-secondary/10",
    },
  ];

  const stats = canSeeStats ? [...baseStats, ...extraStats] : baseStats;

  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 ${
        canSeeStats ? "lg:grid-cols-5" : "lg:grid-cols-2"
      } gap-6 mb-8`}
    >
      {stats.map((stat) => (
        <div
          key={stat.id}
          className={`bg-background border border-border rounded-lg p-6 hover-elevate transition-all duration-200 ${
            stat.id === "total" && onOpenTotal ? "cursor-pointer" : ""
          }`}
          role={stat.id === "total" && onOpenTotal ? "button" : undefined}
          tabIndex={stat.id === "total" && onOpenTotal ? 0 : undefined}
          onClick={() => {
            if (stat.id === "total") onOpenTotal?.();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (stat.id === "total") onOpenTotal?.();
            }
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}
            >
              <Icon name={stat.icon} size={24} className={stat.color} />
            </div>
          </div>

          <div>
            <p className="text-2xl font-bold text-foreground mb-1">
              {typeof stat.value === "number"
                ? stat.value.toLocaleString("ro-RO")
                : stat.value}
            </p>
            <p className="text-sm text-text-secondary">{stat.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ServiceStats;
